import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import { getAllTables } from '../services/tableService';
import {
  QrCode, Download, RefreshCw, Copy, Check, Store,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { downloadQrCard } from '../lib/qrCard';

/** QRCodes: every code a guest can scan — one per table, and the branch's own table-less code for a guest's phone. */
export default function QRCodes() {
  const { restaurantId, restaurant, location } = useRestaurant();
  const { t } = useT();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrByTable, setQrByTable] = useState({});
  const [busy, setBusy] = useState({});
  const [copiedTable, setCopiedTable] = useState(null);
  /* WHICH table's code is on screen. Every table is on show as a tile — the
     floor at a glance, ticked where a code already exists — but only the
     chosen one carries a QR panel: a branch with forty tables was forty
     near-identical placeholders to scroll past. The whole list folds away
     behind one Show/Hide for when the panel is all you want to look at. */
  const [pickedTableId, setPickedTableId] = useState(null);
  const [tablesOpen, setTablesOpen] = useState(true);

  /* Customer Self Service QR: the branch's code with NO table on it. A guest
     opens it on their own phone, orders and pays without ever being asked for
     a table number, and the order files on the branch's ONLINE counter as a
     guest-phone order. A kiosk is a different thing - the RestoBOXX Kiosk
     application on a counter machine, registered and named under Settings >
     Devices - and needs no code from here. */
  const [kioskQr, setKioskQr] = useState(null);
  const [kioskBusy, setKioskBusy] = useState(false);
  const [kioskCopied, setKioskCopied] = useState(false);


  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getAllTables(restaurantId, { isActive: true });
        if (!cancelled) {
          const rows = data?.tables || [];
          setTables(rows);
          // Land on the first table so the panel is never an empty frame.
          setPickedTableId((cur) => (cur ?? (rows[0] ? rows[0].id : null)));
          setError('');
        }
      } catch (err) {
        if (!cancelled) setError(err.error || t('qr.error.tables'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Downloads leave as designed CARDS (lib/qrCard): the restaurant's name,
     what the code opens, and the code on a poster — not a bare code on
     white. If composing fails for any reason, the plain PNG still saves. */
  const plainDownload = (href, name) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const restaurantTitle = restaurant?.name || t('qr.card.restaurantFallback');

  /* The branch's table-less code. `base` is the site the admin is on, so the
     link is built against the address guests can actually reach - a code
     generated on localhost is useless printed on a counter card. */
  const generateKioskQr = async () => {
    setKioskBusy(true);
    try {
      const origin = window.location.origin;
      const loc = location?.id ? `&location=${location.id}` : '';
      const res = await fetch(
        `${API_URL}/users/restaurant/${restaurantId}/kiosk-qr/?base=${encodeURIComponent(origin)}${loc}`
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || t('qr.error.kiosk'));
      } else {
        setKioskQr(data.data);
        setError('');
      }
    } catch (err) {
      setError(t('qr.error.kioskNetwork'));
    } finally {
      setKioskBusy(false);
    }
  };

  const copyKioskLink = async () => {
    if (!kioskQr?.url) return;
    try {
      await navigator.clipboard.writeText(kioskQr.url);
      setKioskCopied(true);
      setTimeout(() => setKioskCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is on screen to copy by hand */
    }
  };

  const downloadKioskQr = async () => {
    if (!kioskQr?.qr_image) return;
    try {
      await downloadQrCard({
        qrDataUrl: kioskQr.qr_image,
        title: restaurantTitle,
        subtitle: t('qr.kiosk.cardSubtitle'),
        hint: t('qr.card.hint'),
        hint2: t('qr.kiosk.cardHint2'),
        footnote: kioskQr.location_name || location?.name || '',
        fileName: 'self-service-qr.png',
      });
    } catch {
      plainDownload(kioskQr.qr_image, 'self-service-qr.png');
    }
  };

  const generateQr = async (table) => {
    setBusy((b) => ({ ...b, [table.id]: true }));
    try {
      const origin = window.location.origin;
      // "Regenerate" rotates the table's QR slug, invalidating printed codes; a first "Generate" keeps the link stable.
      const rotate = qrByTable[table.id] ? '&rotate=1' : '';
      const res = await fetch(
        `${API_URL}/users/restaurant/${restaurantId}/tables/${table.id}/qr/?base=${encodeURIComponent(origin)}${rotate}`
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || t('qr.error.tableQr'));
      } else {
        setQrByTable((m) => ({ ...m, [table.id]: data.data }));
        setError('');
      }
    } catch (err) {
      setError(t('qr.error.tableQrNetwork'));
    } finally {
      setBusy((b) => ({ ...b, [table.id]: false }));
    }
  };

  const copyLink = async (table) => {
    const qr = qrByTable[table.id];
    if (!qr) return;
    try {
      await navigator.clipboard.writeText(qr.url);
      setCopiedTable(table.id);
      setTimeout(() => setCopiedTable(null), 1500);
    } catch {
      /* ignore */
    }
  };

  const downloadQr = async (table) => {
    const qr = qrByTable[table.id];
    if (!qr?.qr_image) return;
    const code = table.tableCode || table.id;
    try {
      await downloadQrCard({
        qrDataUrl: qr.qr_image,
        title: restaurantTitle,
        subtitle: t('qr.table', { code }),
        hint: t('qr.card.hint'),
        hint2: t('qr.card.tableHint2'),
        footnote: location?.name || '',
        fileName: `table-${code}-qr.png`,
      });
    } catch {
      plainDownload(qr.qr_image, `table-${code}-qr.png`);
    }
  };

  return (
    <div className="p-6 w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
            <QrCode className="h-6 w-6 text-amber-600" />
            {t('qr.title')}
          </h1>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Customer Self Service QR: the branch's own code — order from your
          phone with no table number at all. One per branch, and it is not a
          kiosk: the kiosk is the counter application, registered in Devices. */}
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Store className="h-5 w-5 text-amber-600" />
          <h2 className="font-semibold text-gray-900">{t('qr.kiosk.title')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{t('qr.kiosk.blurb')}</p>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Button onClick={generateKioskQr} disabled={kioskBusy}>
              <QrCode className={`h-4 w-4 mr-1.5 ${kioskBusy ? 'animate-pulse' : ''}`} />
              {kioskQr ? t('qr.regenerate') : t('qr.kiosk.generate')}
            </Button>

            {kioskQr && (
              <div className="mt-4 space-y-2">
                <div className="text-xs text-muted-foreground">
                  {t('qr.kiosk.branch', {
                    name: kioskQr.location_name || location?.name || '—',
                  })}
                </div>
                <div className="text-xs text-muted-foreground break-all bg-gray-50 rounded p-2">
                  {kioskQr.url}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyKioskLink}>
                    {kioskCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        {t('qr.copied')}
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        {t('qr.copy')}
                      </>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadKioskQr}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    {t('qr.download')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {kioskQr && (
            <div className="flex items-center justify-center">
              <img
                src={kioskQr.qr_image}
                alt={t('qr.kiosk.alt')}
                className="h-44 w-44 object-contain bg-white rounded shadow-sm border border-gray-100"
              />
            </div>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mx-auto" />
          <p className="mt-3 text-sm text-gray-500">{t('qr.tables.loading')}</p>
        </div>
      ) : tables.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {t('qr.tables.empty')}
        </Card>
      ) : (
        (() => {
          /* Every table on screen at once: a tile each, carrying the room and
             the seat count so two tables numbered 4 in different rooms are
             told apart, and ticked where a code already exists — "has T15 got
             a QR yet?" is answered by looking rather than by generating one to
             find out. Only the chosen tile fills the QR panel below, and the
             whole grid folds away behind Show/Hide on a forty-table floor. */
          const table = tables.find((x) => x.id === pickedTableId) || tables[0];
          if (!table) return null;
          const qr = qrByTable[table.id];
          const code = table.tableCode || table.id;
          const where = table.room?.name || t('qr.tables.noRoom');
          const seats = table.capacity ? t('qr.tables.seats', { n: table.capacity }) : '';
          return (
            <Card className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <QrCode className="h-5 w-5 text-amber-600" />
                  <h2 className="font-semibold text-gray-900">{t('qr.tables.title')}</h2>
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                    {t('qr.tables.count', { n: tables.length })}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setTablesOpen((o) => !o)}
                  aria-expanded={tablesOpen}
                  aria-controls="qr-table-grid"
                >
                  {tablesOpen ? (
                    <ChevronUp className="h-4 w-4 mr-1.5" />
                  ) : (
                    <ChevronDown className="h-4 w-4 mr-1.5" />
                  )}
                  {tablesOpen ? t('qr.tables.hide') : t('qr.tables.show')}
                </Button>
              </div>

              {tablesOpen && (
                <div id="qr-table-grid">
                  <p className="mt-3 mb-2 text-sm text-muted-foreground">{t('qr.tables.pick')}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2">
                    {tables.map((row) => {
                      const c = row.tableCode || row.id;
                      const rowWhere = row.room?.name || t('qr.tables.noRoom');
                      const rowSeats = row.capacity
                        ? t('qr.tables.seats', { n: row.capacity }) : '';
                      const picked = row.id === table.id;
                      return (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => setPickedTableId(row.id)}
                          aria-pressed={picked}
                          className={`relative rounded-md border px-3 py-2 text-left transition-colors
                            focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                            picked
                              ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300'
                              : 'border-input bg-background hover:border-amber-300 hover:bg-amber-50/40'
                          }`}
                        >
                          {qrByTable[row.id] && (
                            <Check
                              className="absolute top-1.5 right-1.5 h-3.5 w-3.5 text-emerald-600"
                              aria-label={t('qr.tables.ready')}
                            />
                          )}
                          <div className="truncate pr-4 text-sm font-medium text-gray-900">
                            {t('qr.table', { code: c })}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {rowWhere}{rowSeats ? ` · ${rowSeats}` : ''}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {t('qr.table', { code })}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {where}{seats ? ` · ${seats}` : ''}
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => generateQr(table)}
                  disabled={!!busy[table.id]}
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy[table.id] ? 'animate-spin' : ''}`} />
                  {qr ? t('qr.regenerate') : t('qr.generate')}
                </Button>
              </div>

              <div className="mt-4 flex items-center justify-center bg-amber-50/40 rounded-md border border-dashed border-amber-200 min-h-[220px]">
                {qr ? (
                  <img
                    src={qr.qr_image}
                    alt={t('qr.tables.alt', { code })}
                    className="h-52 w-52 object-contain bg-white rounded shadow-sm"
                  />
                ) : (
                  <div className="text-xs text-muted-foreground text-center px-4 py-8">
                    <QrCode className="h-10 w-10 text-amber-300 mx-auto mb-2" />
                    {t('qr.tables.hintBefore')} <strong>{t('qr.generate')}</strong>{' '}
                    {t('qr.tables.hintAfter')}
                  </div>
                )}
              </div>

              {qr && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-muted-foreground break-all bg-gray-50 rounded p-2">
                    {qr.url}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => copyLink(table)}
                    >
                      {copiedTable === table.id ? (
                        <>
                          <Check className="h-3.5 w-3.5 mr-1.5" />
                          {t('qr.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5 mr-1.5" />
                          {t('qr.copy')}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => downloadQr(table)}
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      {t('qr.download')}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          );
        })()
      )}
    </div>
  );
}
